import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { useReactToPrint } from 'react-to-print';
import useAuth from '../../hooks/useAuth';
import DashboardService from '../../services/http/services/DashboardService';
import CitiesService from '../../services/http/services/CitiesService';
import { EmitError } from '../../utils/EmitError';
import {
    IDashboardInvoicesByTypeItem,
    IDashboardInvoicesImportHistoryItem,
    IDashboardRegistrationHistoryItem,
    IDashboardTopCompanyItem,
    IDashboardUpcomingRaffle,
    IDashboardTopUserItem,
    IDashboardSocialInstitutionsData,
    IDashboardFinishedRaffle,
} from '../../services/http/mappers/DashboardMapper';
import { styledReportDashboard } from './styledReportDashboard';

import { EModule } from '../../enums';

export type RegistrationPeriod = 'day' | 'month' | 'year';

export default function useDashboard() {
    const componentToPrintRef = useRef<HTMLDivElement>(null);
    const [buttonReportDisabled, setButtonReportDisabled] = useState(true);
    const [isReportReady, setIsReportReady] = useState(false);

    const handlePrint = useReactToPrint({
        content: () => componentToPrintRef.current,
        documentTitle: 'Relatório do Dashboard',
        pageStyle: styledReportDashboard,
        onBeforePrint: () => setButtonReportDisabled(true),
        onAfterPrint: () => setButtonReportDisabled(false),
    });

    const [totalUsers, setTotalUsers] = useState<number>(0);
    const [totalInvoices, setTotalInvoices] = useState<number>(0);
    const [totalInvoicesValue, setTotalInvoicesValue] = useState<number>(0);
    const [totalPendingInvoices, setTotalPendingInvoices] = useState<number>(0);
    const [invoicesStats, setInvoicesStats] = useState({ totalValid: 0, totalValue: 0 });
    const [invoicesByType, setInvoicesByType] = useState<IDashboardInvoicesByTypeItem[]>([]);
    const [invoicesImportHistory, setInvoicesImportHistory] = useState<IDashboardInvoicesImportHistoryItem[]>([]);
    const [allUpcomingRaffles, setAllUpcomingRaffles] = useState<IDashboardUpcomingRaffle[]>([]);
    const [latestPendingRaffles, setLatestPendingRaffles] = useState<IDashboardUpcomingRaffle[]>([]);
    const [topCompanies, setTopCompanies] = useState<IDashboardTopCompanyItem[]>([]);
    const [topCompaniesAll, setTopCompaniesAll] = useState<IDashboardTopCompanyItem[]>([]);
    const [topCompaniesNFC, setTopCompaniesNFC] = useState<IDashboardTopCompanyItem[]>([]);
    const [topCompaniesNFS, setTopCompaniesNFS] = useState<IDashboardTopCompanyItem[]>([]);
    const [topCompaniesNFE, setTopCompaniesNFE] = useState<IDashboardTopCompanyItem[]>([]);
    const [topUsers, setTopUsers] = useState<IDashboardTopUserItem[]>([]);
    const [topUsersAll, setTopUsersAll] = useState<IDashboardTopUserItem[]>([]);
    const [topUsersNFC, setTopUsersNFC] = useState<IDashboardTopUserItem[]>([]);
    const [topUsersNFS, setTopUsersNFS] = useState<IDashboardTopUserItem[]>([]);
    const [topUsersNFE, setTopUsersNFE] = useState<IDashboardTopUserItem[]>([]);
    const [typeTopUserSelected, setTypeTopUserSelected] = useState<string>('todas');
    const [sortByTopUser, setSortByTopUser] = useState<'total' | 'tickets' | 'value'>('total');
    const [isLoadingTopUsers, setIsLoadingTopUsers] = useState(false);
    const [socialInstitutionsStats, setSocialInstitutionsStats] = useState<IDashboardSocialInstitutionsData>({
        total: 0,
        totalActive: 0,
        totalLinkedUsers: 0,
        institutions: [],
    });
    const [finishedRaffles, setFinishedRaffles] = useState<IDashboardFinishedRaffle[]>([]);
    const [registrationHistory, setRegistrationHistory] = useState<IDashboardRegistrationHistoryItem[]>([]);
    const [registrationPeriod, setRegistrationPeriod] = useState<RegistrationPeriod>('month');
    const [typeInvoiceSelected, setTypeInvoiceSelected] = useState<string>('todas');
    const [isLoading, setIsLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [allYears, setAllYears] = useState(true);
    const [selectedRaffleId, setSelectedRaffleId] = useState<number | 'all'>('all');
    const { activeCity, user } = useAuth();
    const [selectedCityId, setSelectedCityId] = useState<number | undefined>(activeCity?.id);
    const activeCityIdRef = useRef(activeCity?.id);

    const [allCities, setAllCities] = useState<{ id: number; name: string; uf: string; modules?: EModule[] }[]>([]);

    const isAdmin = user?.role?.id === 1;
    const availableCities = useMemo(() => (isAdmin ? allCities : (user?.cities ?? [])), [isAdmin, allCities, user?.cities]);

    const allCombinedRaffles = useMemo(() => {
        const map = new Map<number, IDashboardUpcomingRaffle | IDashboardFinishedRaffle>();
        for (const r of allUpcomingRaffles) {
            if (r.id) map.set(r.id, r);
        }
        for (const r of latestPendingRaffles) {
            if (r.id && !map.has(r.id)) map.set(r.id, r);
        }
        for (const r of finishedRaffles) {
            if (r.id && !map.has(r.id)) map.set(r.id, r as any);
        }
        return Array.from(map.values());
    }, [allUpcomingRaffles, latestPendingRaffles, finishedRaffles]);

    const selectedRaffle = useMemo(() => {
        if (selectedRaffleId === 'all') return undefined;
        return allCombinedRaffles.find(r => r.id === selectedRaffleId);
    }, [selectedRaffleId, allCombinedRaffles]);

    const startDate = selectedRaffle?.startPeriod;
    const endDate = selectedRaffle?.endPeriod;
    const year = allYears ? undefined : selectedYear;

    const dateFilterParams = useMemo(() => {
        if (startDate && endDate) {
            return {
                startDate,
                endDate,
                startPeriod: startDate,
                endPeriod: endDate,
            };
        }
        return {
            year,
        };
    }, [startDate, endDate, year]);

    const checkCityHasSocialInstitution = useCallback((cityObj?: { modules?: any }): boolean => {
        if (!cityObj || !cityObj.modules) return false;
        let mods = cityObj.modules;
        if (typeof mods === 'string') {
            try {
                mods = JSON.parse(mods);
            } catch {
                return false;
            }
        }
        if (Array.isArray(mods)) {
            return (mods as any[]).includes(EModule.SOCIAL_INSTITUTION) || (mods as any[]).includes('social_institution');
        }
        return false;
    }, []);

    const hasSocialInstitutionModule = useMemo(() => {
        const hasData = Boolean(
            (socialInstitutionsStats?.total && socialInstitutionsStats.total > 0) ||
            (socialInstitutionsStats?.institutions && socialInstitutionsStats.institutions.length > 0) ||
            (socialInstitutionsStats?.totalLinkedUsers && socialInstitutionsStats.totalLinkedUsers > 0)
        );

        if (hasData) {
            return true;
        }

        if (isAdmin) {
            if (!selectedCityId) {
                if (availableCities.length === 0) return true;
                return availableCities.some(c => checkCityHasSocialInstitution(c));
            }
            const found = (availableCities as Array<{ id?: number; modules?: any }>).find(c => c.id === selectedCityId);
            if (found && checkCityHasSocialInstitution(found)) {
                return true;
            }
            if (activeCity && activeCity.id === selectedCityId && checkCityHasSocialInstitution(activeCity)) {
                return true;
            }
            return false;
        }

        return checkCityHasSocialInstitution(activeCity);
    }, [isAdmin, selectedCityId, availableCities, activeCity, socialInstitutionsStats, checkCityHasSocialInstitution]);

    useEffect(() => {
        if (!isAdmin) return;
        const controller = new AbortController();
        CitiesService.listCitiesBasic(controller.signal)
            .then(setAllCities)
            .catch(() => { });
        return () => controller.abort();
    }, [isAdmin]);

    useEffect(() => {
        if (isAdmin && activeCity?.id !== activeCityIdRef.current) {
            setSelectedCityId(activeCity?.id);
        }
        activeCityIdRef.current = activeCity?.id;
    }, [isAdmin, activeCity?.id]);

    const handleAllYears = useCallback(() => {
        setAllYears(true);
        setSelectedYear(new Date().getFullYear());
        setSelectedRaffleId('all');
        setRegistrationPeriod('month');
    }, []);

    const handleSelectYear = useCallback((year: number) => {
        setAllYears(false);
        setSelectedYear(year);
        setSelectedRaffleId('all');
        setRegistrationPeriod('month');
    }, []);

    const handleSelectCity = useCallback((cityId: number | undefined) => {
        setSelectedCityId(cityId);
        setSelectedRaffleId('all');
        setRegistrationPeriod('month');
    }, []);

    const handleSelectRaffle = useCallback((raffleId: number | 'all') => {
        setSelectedRaffleId(raffleId);
        if (raffleId !== 'all') {
            setRegistrationPeriod('day');
        } else {
            setRegistrationPeriod('month');
        }
    }, []);

    const effectiveCityId = isAdmin ? selectedCityId : activeCity?.id;

    // Fetch raffles list independently when city changes
    useEffect(() => {
        if (!isAdmin && !activeCity?.id) return;
        const controller = new AbortController();

        async function fetchRafflesList() {
            try {
                const [allRaffles, latestPending] = await Promise.all([
                    DashboardService.getAllUpcomingRaffles({ countyId: effectiveCityId, signal: controller.signal }),
                    DashboardService.getLatestPendingRaffles({ countyId: effectiveCityId, signal: controller.signal }).catch(() => [] as IDashboardUpcomingRaffle[]),
                ]);
                setAllUpcomingRaffles(allRaffles);
                setLatestPendingRaffles(latestPending);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") return;
            }
        }

        fetchRafflesList();

        return () => controller.abort();
    }, [effectiveCityId, isAdmin, activeCity?.id]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadDashboard() {
            try {
                setIsLoading(true);
                setIsReportReady(false);
                setButtonReportDisabled(true);

                const [
                    users,
                    invoices,
                    pendingInvoices,
                    stats,
                    byType,
                    importHistory,
                    socialInstitutions,
                    finishedRafflesData,
                ] = await Promise.all([
                    DashboardService.getTotalUsers({ countyId: effectiveCityId, ...dateFilterParams, signal: controller.signal }),
                    DashboardService.getTotalInvoices({ countyId: effectiveCityId, ...dateFilterParams, signal: controller.signal }),
                    DashboardService.getTotalPendingInvoices({ countyId: effectiveCityId, ...dateFilterParams, signal: controller.signal }),
                    DashboardService.getInvoicesStats({ countyId: effectiveCityId, ...dateFilterParams, signal: controller.signal }),
                    DashboardService.getInvoicesByType({ countyId: effectiveCityId, ...dateFilterParams, signal: controller.signal }),
                    DashboardService.getInvoicesImportHistory({ countyId: effectiveCityId, ...dateFilterParams, signal: controller.signal }),
                    DashboardService.getSocialInstitutionsStats({ countyId: effectiveCityId, ...dateFilterParams, signal: controller.signal }).catch(() => ({ total: 0, totalActive: 0, totalLinkedUsers: 0, institutions: [] })),
                    DashboardService.getFinishedRaffles({ countyId: effectiveCityId, ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardFinishedRaffle[]),
                ]);

                setTotalUsers(users.totalUsers);
                setTotalInvoices(invoices.totalInvoices);
                setTotalInvoicesValue(invoices.totalValue);
                setTotalPendingInvoices(pendingInvoices.totalPendingInvoices);
                setInvoicesStats(stats);
                setInvoicesByType(byType);
                setInvoicesImportHistory(importHistory);
                setSocialInstitutionsStats(socialInstitutions);
                setFinishedRaffles(finishedRafflesData);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") return;
                EmitError(err, true, "dashboard_load");
            } finally {
                setIsLoading(false);
                setTimeout(() => {
                    setIsReportReady(true);
                    setButtonReportDisabled(false);
                }, 2500);
            }
        }

        if (isAdmin || activeCity?.id) {
            loadDashboard();
        }

        return () => controller.abort();
    }, [effectiveCityId, startDate, endDate, year, dateFilterParams, isAdmin, activeCity?.id]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadRegistrationHistory() {
            try {
                const history = await DashboardService.getRegistrationHistory({
                    countyId: effectiveCityId,
                    period: registrationPeriod,
                    ...dateFilterParams,
                    signal: controller.signal,
                });

                setRegistrationHistory(history);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") return;
                EmitError(err, true, "dashboard_load_registration_history");
            }
        }

        if (isAdmin || activeCity?.id) {
            loadRegistrationHistory();
        }

        return () => controller.abort();
    }, [effectiveCityId, registrationPeriod, startDate, endDate, year, dateFilterParams, isAdmin, activeCity?.id]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadTopCompaniesByType() {
            try {
                const [all, nfc, nfs, nfe] = await Promise.all([
                    DashboardService.getTopCompanies({ countyId: effectiveCityId, type: 'todas', ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardTopCompanyItem[]),
                    DashboardService.getTopCompanies({ countyId: effectiveCityId, type: 'NFC', ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardTopCompanyItem[]),
                    DashboardService.getTopCompanies({ countyId: effectiveCityId, type: 'NFS', ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardTopCompanyItem[]),
                    DashboardService.getTopCompanies({ countyId: effectiveCityId, type: 'NFE', ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardTopCompanyItem[]),
                ]);
                setTopCompaniesAll(all);
                setTopCompaniesNFC(nfc);
                setTopCompaniesNFS(nfs);
                setTopCompaniesNFE(nfe);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') return;
            }
        }

        if (isAdmin || activeCity?.id) loadTopCompaniesByType();
        return () => controller.abort();
    }, [effectiveCityId, startDate, endDate, year, dateFilterParams, isAdmin, activeCity?.id]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadTopCompanies() {
            try {
                const companies = await DashboardService.getTopCompanies({
                    countyId: effectiveCityId,
                    type: typeInvoiceSelected,
                    ...dateFilterParams,
                    signal: controller.signal,
                });

                setTopCompanies(companies);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") return;
                EmitError(err, true, "dashboard_load_top_companies");
            }
        }

        if (isAdmin || activeCity?.id) {
            loadTopCompanies();
        }

        return () => controller.abort();
    }, [effectiveCityId, typeInvoiceSelected, startDate, endDate, year, dateFilterParams, isAdmin, activeCity?.id]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadTopUsersByType() {
            try {
                const [all, nfc, nfs, nfe] = await Promise.all([
                    DashboardService.getTopUsers({ countyId: effectiveCityId, type: 'todas', limit: 10, ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardTopUserItem[]),
                    DashboardService.getTopUsers({ countyId: effectiveCityId, type: 'NFC', limit: 10, ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardTopUserItem[]),
                    DashboardService.getTopUsers({ countyId: effectiveCityId, type: 'NFS', limit: 10, ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardTopUserItem[]),
                    DashboardService.getTopUsers({ countyId: effectiveCityId, type: 'NFE', limit: 10, ...dateFilterParams, signal: controller.signal }).catch(() => [] as IDashboardTopUserItem[]),
                ]);
                setTopUsersAll(all);
                setTopUsersNFC(nfc);
                setTopUsersNFS(nfs);
                setTopUsersNFE(nfe);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') return;
            }
        }

        if (isAdmin || activeCity?.id) loadTopUsersByType();
        return () => controller.abort();
    }, [effectiveCityId, startDate, endDate, year, dateFilterParams, isAdmin, activeCity?.id]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadTopUsers() {
            try {
                setIsLoadingTopUsers(true);

                const users = await DashboardService.getTopUsers({
                    countyId: effectiveCityId,
                    type: typeTopUserSelected,
                    limit: 10,
                    orderBy: sortByTopUser,
                    ...dateFilterParams,
                    signal: controller.signal,
                });

                setTopUsers(users);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") return;
                EmitError(err, true, "dashboard_load_top_users");
            } finally {
                setIsLoadingTopUsers(false);
            }
        }

        if (isAdmin || activeCity?.id) {
            loadTopUsers();
        }

        return () => controller.abort();
    }, [effectiveCityId, typeTopUserSelected, sortByTopUser, startDate, endDate, year, dateFilterParams, isAdmin, activeCity?.id]);

    return {
        totalUsers,
        totalInvoices,
        totalInvoicesValue,
        totalPendingInvoices,
        invoicesStats,
        invoicesByType,
        invoicesImportHistory,
        allUpcomingRaffles,
        allCombinedRaffles,
        latestPendingRaffles,
        topCompanies,
        topCompaniesAll,
        topCompaniesNFC,
        topCompaniesNFS,
        topCompaniesNFE,
        topUsers,
        topUsersAll,
        topUsersNFC,
        topUsersNFS,
        topUsersNFE,
        typeTopUserSelected,
        setTypeTopUserSelected,
        sortByTopUser,
        setSortByTopUser,
        isLoadingTopUsers,
        socialInstitutionsStats,
        hasSocialInstitutionModule,
        finishedRaffles,
        registrationHistory,
        registrationPeriod,
        setRegistrationPeriod,
        isLoading,
        isReportReady,
        selectedYear,
        allYears,
        selectedRaffleId,
        selectedRaffle,
        handleSelectYear,
        handleAllYears,
        handleSelectRaffle,
        isAdmin,
        availableCities,
        selectedCityId,
        handleSelectCity,
        typeInvoiceSelected,
        setTypeInvoiceSelected,
        componentToPrintRef,
        handlePrint,
        buttonReportDisabled,
    };
}
